import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

/** Fetch a report endpoint, re-fetching whenever the population changes.
 *
 * `errorStatus` carries the HTTP status of a failed request (e.g. 404) so a
 * caller can distinguish an expected "nothing here yet" condition from a
 * genuine failure (500, network error) without parsing the message string.
 * It is additive -- existing callers that only destructure
 * `{ data, loading, error }` are unaffected. */
export default function useReport(path, population, extraQuery = '') {
  const { API_URL } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setErrorStatus(null);
      // Clear the previous request's payload as soon as a new one starts.
      // Without this, a failed or empty response for the NEW path/query
      // leaves the OLD payload sitting in `data` -- and a caller that masks
      // its error state for an expected condition (e.g. TapHeatmapReport's
      // 404 "no map uploaded yet") would then render that stale payload as
      // if it belonged to the new query. ReportShell hides `children` while
      // `loading` is true, so this has no visible effect on the ordinary
      // success path -- it only matters once loading finishes without a
      // fresh payload to show.
      setData(null);
      try {
        const res = await axios.get(
          `${API_URL}/admin/reports/${path}?population=${population}${extraQuery}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Failed to load report');
          setErrorStatus(err?.response?.status ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [API_URL, path, population, extraQuery]);

  return { data, loading, error, errorStatus };
}
